import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import ThemedSelect from 'components/ThemedSelect';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetAllAccounts } from 'pages/app/Credits/gql/queries/useGetAllAccounts';
import { AppContext } from 'shared/AppContext';
import { useGetAccountPurchases } from '../gql/useGetAccountPurchases';
import usePurchasesExport from '../hooks/usePurchasesExport';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  FilterRow,
  FilterContainer,
} from './StyledComponents';

const GET_ANNUAL_TICKETS_FOR_FILTER = gql`
  query GetAnnualTickets($getAnnualTicketsInput: GetAnnualTicketsInput!) {
    getAnnualTickets(input: $getAnnualTicketsInput) {
      count
      results {
        id
        name
        ticket {
          id
        }
      }
    }
  }
`;

// Receipt icon for page header
const ReceiptIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6m-6 4h6"
    />
  </svg>
);

const PurchasesPageContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;
  const { id: accountIdParam, name: accountName } = useParams();

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const userType = isTrackAdmin
    ? 'track'
    : isEmployeeAdmin
    ? 'employee'
    : 'admin';

  const {
    state: { accountId: contextAccountId },
  } = useContext(AppContext);

  // Use accountId from URL params or context
  const urlAccountId = accountIdParam ? parseInt(accountIdParam) : null;
  const displayName = accountName ? decodeURIComponent(accountName) : null;

  // Get current year
  const currentYear = Number(moment().format('YYYY'));

  // Filter state
  const [accountFilter, setAccountFilter] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [ticketFilter, setTicketFilter] = useState(null);

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Get accounts for filter (admin only)
  const { data: accountsData } = useGetAllAccounts();

  // Pre-select account filter from URL params
  useEffect(() => {
    if (urlAccountId && accountsData?.getAllAccounts && !accountFilter) {
      const account = accountsData.getAllAccounts.find(
        (acc) => acc.id === urlAccountId
      );
      if (account) {
        setAccountFilter({ value: account.id, label: account.business_dba });
      }
    }
  }, [urlAccountId, accountsData, accountFilter]);

  // Effective account ID for queries
  const effectiveAccountId =
    userType === 'admin'
      ? accountFilter
        ? accountFilter.value
        : null
      : contextAccountId;

  // Fetch tickets for filter dropdown
  const { data: ticketsData } = useQuery(GET_ANNUAL_TICKETS_FOR_FILTER, {
    variables: {
      getAnnualTicketsInput: {
        accountFilter: effectiveAccountId,
        year,
      },
    },
    skip: !effectiveAccountId,
  });

  const ticketOptions = (ticketsData?.getAnnualTickets?.results || [])
    .filter((t) => t.ticket?.id)
    .map((t) => ({ value: t.ticket.id, label: t.name }));

  // Fetch purchases
  const { data, loading } = useGetAccountPurchases({
    accountId: effectiveAccountId,
    year,
    ticketId: ticketFilter ? ticketFilter.value : null,
  });

  const purchases = data?.getAccountPurchases?.results ?? [];
  const totalCount = data?.getAccountPurchases?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Queue-based export
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    usePurchasesExport();
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const jobId = await startExport(
          {
            accountId: effectiveAccountId,
            year,
            search: input.queryString || null,
            ticketId: ticketFilter ? ticketFilter.value : null,
          },
          format
        );

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} transactions (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } transactions exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Failed to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      startExport,
      subscribeToProgress,
      getDownloadUrl,
      effectiveAccountId,
      year,
      input.queryString,
      ticketFilter,
    ]
  );

  // Account options for filter
  const accountOptions = (accountsData?.getAllAccounts || []).map((acc) => ({
    value: acc.id,
    label: acc.business_dba,
  }));

  // Year options
  const yearOptions = [
    { value: currentYear - 2, label: String(currentYear - 2) },
    { value: currentYear - 1, label: String(currentYear - 1) },
    { value: currentYear, label: String(currentYear) },
    { value: currentYear + 1, label: String(currentYear + 1) },
  ];

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    updateQuery({ queryString: searchTerm, page: 1 });
  };

  // Table columns — transaction data
  const columns = [
    {
      field: 'purchase_date',
      header: 'Date/Time',
      render: (row) => {
        if (!row.purchase_date) return '—';
        return moment(Number(row.purchase_date)).format('M/D/YY h:mm A');
      },
    },
    {
      field: 'purchaser',
      header: 'Purchaser',
      render: (row) => row.user?.formatted_name || `Transaction #${row.id}`,
    },
    {
      field: 'subtotal',
      header: 'Total',
      headerStyle: { width: '70px' },
      render: (row) => row.subtotal || '$0.00',
    },
    {
      field: 'discount',
      header: 'Disc.',
      headerStyle: { width: '60px' },
      render: (row) => row.discount || '$0.00',
    },
    {
      field: 'refund',
      header: 'Refund',
      headerStyle: { width: '60px' },
      render: (row) => row.refund || '$0.00',
    },
    {
      field: 'credit',
      header: 'Credit',
      headerStyle: { width: '60px' },
      render: (row) => row.credit || '$0.00',
    },
    {
      field: 'fee',
      header: 'Fee',
      headerStyle: { width: '55px' },
      render: (row) => row.fee || '$0.00',
    },
    {
      field: 'total',
      header: 'Trans. Total',
      headerStyle: { width: '80px' },
      render: (row) => row.total || '$0.00',
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'View Transaction',
      onClick: (row) =>
        history.push(`${routePrefix}/transactions/detail/${row.id}`),
    },
  ];

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon>
              <ReceiptIcon />
            </PageHeaderIcon>
            <HeaderTitle>
              {displayName
                ? `${displayName} - Purchases`
                : 'Annual / Membership Purchases'}
            </HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Data Table Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Search + Filters Row */}
                <PageHeader>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by last name..."
                  />
                  <FilterRow>
                    <FilterContainer>
                      <ThemedSelect
                        placeholder="Year"
                        options={yearOptions}
                        value={{ value: year, label: String(year) }}
                        onChange={(selected) =>
                          setYear(selected?.value || currentYear)
                        }
                      />
                    </FilterContainer>
                    <FilterContainer>
                      <ThemedSelect
                        placeholder="All Tickets"
                        options={ticketOptions}
                        value={ticketFilter}
                        onChange={(selected) => {
                          setTicketFilter(selected);
                          updateQuery({ page: 1 });
                        }}
                        isClearable
                      />
                    </FilterContainer>
                    {userType === 'admin' && !urlAccountId && (
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Select Account"
                          options={accountOptions}
                          value={accountFilter}
                          onChange={(selected) => setAccountFilter(selected)}
                          isClearable
                        />
                      </FilterContainer>
                    )}
                  </FilterRow>
                </PageHeader>

                <DataTable
                  columns={columns}
                  data={purchases}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No purchases found"
                  emptySubtext="Try adjusting your filters"
                  emptyIcon={
                    <svg
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6m-6 4h6"
                      />
                    </svg>
                  }
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            <QuickActions
              title="Quick Actions"
              actions={[
                ...(totalCount > 0
                  ? [
                      {
                        label: 'Export Purchases',
                        icon: (
                          <svg
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                          </svg>
                        ),
                        onClick: () => setShowExportModal(true),
                      },
                    ]
                  : []),
              ]}
            />
          </Sidebar>
        </DashboardLayout>
      </Container>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        recordCount={totalCount}
        dataType="purchases"
      />

      {toasterData && (
        <Toaster
          isOpen={!!toasterData}
          onClose={() => setToasterData(null)}
          title={toasterData.title}
          message={toasterData.message}
          type={toasterData.type}
          duration={toasterData.persistent ? 25000 : 10000}
          persistent={toasterData.persistent}
          action={toasterData.action}
        />
      )}
    </PageWrapper>
  );
};

// Wrap with theme provider
const PurchasesPage = () => (
  <EnhancedThemeProvider>
    <PurchasesPageContent />
  </EnhancedThemeProvider>
);

export default PurchasesPage;
